<script setup>
import { computed, ref, watch } from 'vue';
import { modal_2026_ui_styles, close_button_2026_styles } from '~~/shared/utilities/ui';

const props = defineProps({
    headline: {
        type: String,
        default: '',
    },
    // Escape hatches so a single consumer (e.g. the login modal) can override
    // the default headline size / inner container spacing without touching
    // shared styles that every other ModalShell consumer relies on.
    headlineClass: {
        type: String,
        default: '',
    },
    innerClass: {
        type: String,
        default: '',
    },
    // Accessible name / description for the dialog. reka's DialogContent WARNS
    // (dev only) without a DialogTitle and a Description. The visible headline is
    // a styled <h2> in the body, so these feed UModal an sr-only DialogTitle /
    // DialogDescription for screen readers and reka without changing the design.
    // Consumers that render their own header (no `headline`) should pass `title`
    // — otherwise it falls back to the headline.
    title: {
        type: String,
        default: '',
    },
    description: {
        type: String,
        default: '',
    },
    dismissable: {
        type: Boolean,
        default: true,
    },
    persistent: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String,
        default: null,
    },
    // Optional veto hook: called when the modal is about to close (✕ / ESC /
    // backdrop). If it returns false, the modal stays open (the consumer
    // typically swaps to a confirm view instead). Default null = close normally.
    beforeClose: {
        type: Function,
        default: null,
    },
    // Keep the modal mounted but out of sight — for when something else takes
    // the screen and the viewer is expected to come back to this modal intact
    // (Find a Session hides while a session drawer is open on top of it).
    // Deliberately NOT `open = false`: closing resolves the modal's promise and
    // runs the caller's post-close flow, which would end the carousel rather
    // than pause it. Off by default, so no other consumer changes.
    concealed: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['close']);

const open = defineModel('open', { default: true });

// `persistent` always wins: a persistent modal can't be dismissed no matter
// what `dismissable` was passed as.
const canDismiss = computed(() => (props.persistent ? false : props.dismissable));

// Always non-empty so reka's DialogTitle/Description requirements are satisfied
// on every modal. Rendered sr-only via modalUi below.
const dialogTitle = computed(() => props.title || props.headline || 'Dialog');
const dialogDescription = computed(() => props.description || props.headline || dialogTitle.value);

// UModal itself has no `size` variant in this Nuxt UI version — map our
// `size` prop onto a max-width utility merged into the 2026 content class.
const sizeClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
};

// 2026 modal standard (from Figma): purple 80% + 10px-blur overlay, and a
// borderless, rounded, ~900px-wide pink content surface. Overridden here (not
// in the shared modal_2026_ui_styles) so legacy search/registration modals
// stay untouched. `ring-0` kills Nuxt UI's default 1px content ring.
// `invisible` (not `hidden`) so the modal keeps its box and internal state —
// scroll position, the carousel's place — and simply reappears when `concealed`
// goes false. pointer-events-none stops the unseen overlay swallowing clicks
// meant for whatever is on top of it.
const concealClass = computed(() => (props.concealed ? 'invisible pointer-events-none' : ''));

const modalUi = computed(() => {
    const width = props.size && sizeClasses[props.size] ? sizeClasses[props.size] : 'sm:max-w-[56.25rem]';
    return {
        ...modal_2026_ui_styles,
        // z-[999999999999] matches the drawers' "force above everything" rule
        // (app/assets/css/main.css); since a modal opened from within a drawer
        // mounts LATER in the DOM, the equal z-index breaks in the modal's favor,
        // so it renders above the session drawer instead of beneath it.
        overlay: `z-[999999999999] bg-[rgba(130,12,167,0.8)] backdrop-blur-[10px] ${concealClass.value}`,
        // Nuxt UI caps the content at max-h-[calc(100dvh-2rem)] and scrolls the
        // BODY inside it. On a phone that hides the tail of a tall modal (the
        // Close link) behind an internal scroller iOS draws no scrollbar for, so
        // it reads as unreachable. Below `sm` the content goes full-bleed and
        // becomes the scroller itself, so the modal simply runs off the bottom of
        // the screen — the usual "there's more down there" cue — and everything is
        // reachable. Desktop keeps the centred, capped panel.
        content: [
            `z-[999999999999] ${modal_2026_ui_styles.content} ring-0 border-0 rounded-[10px] w-[calc(100%-2rem)] ${width} ${concealClass.value}`,
            // `translate-none`, not translate-x-0/y-0: a zeroed translate is still a
            // translate, and that makes the content a containing block for its
            // position:fixed ✕ — which would then scroll away with the content.
            'max-sm:top-0 max-sm:left-0 max-sm:translate-none max-sm:transform-none',
            'max-sm:w-full max-sm:max-w-none max-sm:h-dvh max-sm:max-h-none',
            'max-sm:rounded-none max-sm:overflow-y-auto',
        ].join(' '),
        // The header renders NOTHING visible: this shell keeps title and
        // description sr-only, and no modal fills #header (only the drawers do,
        // and those are a different component). Nuxt UI still gives it min-h-16
        // — 64px — on top of the 30px padding above. Inside a centred desktop
        // panel that empty band is invisible; on mobile the modal is full-bleed
        // from y=0, so it pushed every modal's first line 64px further down, on
        // top of the body's own 90px. Measured on the Login modal at 430px: the
        // headline sat 179px down, 117px clear of a ✕ that ends at 62px (UNB,
        // second pass — the ✕ blur landed, this padding is what Nick flagged
        // after it).
        // Collapsed below sm, the body's 90px then lands content exactly at the
        // blur strip's lower edge, which is what that 90px was always for.
        header: `${modal_2026_ui_styles.header} max-sm:min-h-0 max-sm:p-0`,
        // Let the content do the scrolling on mobile rather than the body.
        // Figma's 110px side / 60px bottom padding is a desktop measurement — at 110px
        // a 390px phone would be left with ~170px of usable width — so it's applied
        // from `sm` up, and mobile keeps Nuxt UI's default padding.
        // max-sm:pt-[90px] clears the pinned ✕ and its blur strip. Without it the
        // headline starts under both, so the ✕ sits on top of the first line and
        // that line renders permanently blurred rather than only while scrolling.
        body: `${modal_2026_ui_styles.body} sm:px-[110px] sm:pb-[60px] max-sm:overflow-visible max-sm:h-auto max-sm:pt-[90px]`,
        // Close ✕ sits 28px from the top/right corner (Figma header px-28),
        // mirroring the back button — overrides Nuxt UI's default top-4/end-4 (16px).
        // Pinned on mobile so it stays put while the modal scrolls under it.
        close: 'top-[28px] end-[28px] max-sm:fixed',
        // The accessible title/description exist only for reka + screen readers;
        // the design shows the headline as a styled <h2> in the body instead.
        title: 'sr-only',
        description: 'sr-only',
    };
});

// The overlay engine mounts whatever component composes ModalShell with
// `v-model:open` bound to its own promise-resolving ref, and listens for a
// `close` emit to resolve that promise. Watching the local `open` model
// (rather than binding `@update:open` on UModal a second time) means we
// react exactly once per real open -> closed transition, no matter *why* it
// closed (overlay click, Escape, the styled close button, or a consumer
// doing `open.value = false` from inside the default/actions slot).
// `hasEmittedClose` guards against re-emitting if `open` is ever driven to
// `false` more than once before it goes `true` again.
const hasEmittedClose = ref(false);

watch(open, (value) => {
    if (value) {
        hasEmittedClose.value = false;
        return;
    }

    if (hasEmittedClose.value) return;
    hasEmittedClose.value = true;
    emit('close');
});

// Route every close request through the optional veto hook. When beforeClose
// returns false we keep `open` true, so the controlled UModal stays open (no
// flicker — we never let it flip closed). Opening always propagates.
const onUpdateOpen = (value) => {
    if (value) { open.value = true; return; }
    if (props.beforeClose && props.beforeClose() === false) return;
    open.value = false;
};
</script>

<template>
    <UModal
        :open="open"
        @update:open="onUpdateOpen"
        :dismissible="canDismiss"
        :close="close_button_2026_styles"
        :title="dialogTitle"
        :description="dialogDescription"
        :ui="modalUi"
        class="modal-shell-2026"
    >
        <template #body>
            <!-- Mobile only: a blurred strip behind the pinned ✕. The ✕ is
                 `max-sm:fixed` so the modal scrolls under it, which left the glyph
                 sitting directly on top of the headline and unreadable. This bar
                 blurs whatever passes beneath at the same 10px as the overlay.
                 pointer-events-none so it never eats a tap meant for the content,
                 and it stays below the ✕ (which Nuxt UI renders after it). -->
            <div class="modal-shell-2026__topfade sm:hidden" aria-hidden="true" />

            <!-- Back affordance: pinned to the modal content's top-left corner so
                 it lines up with Nuxt UI's close button (absolute top-4 end-4).
                 It's positioned against the content (which is a containing block
                 via its centering transform), so it escapes the body padding and
                 the inner — matching the ✕ on the opposite corner. -->
            <div v-if="$slots['top-left']" class="modal-shell-2026__back">
                <slot name="top-left" />
            </div>
            <div class="modal-shell-2026__inner" :class="innerClass">
                <h2 v-if="headline" class="modal-shell-2026__headline" :class="headlineClass">
                    {{ headline }}
                </h2>

                <div class="modal-shell-2026__body">
                    <slot />
                </div>

                <div v-if="$slots.actions" class="modal-shell-2026__actions">
                    <slot name="actions" />
                </div>
            </div>
        </template>
    </UModal>
</template>

<style>
.modal-shell-2026__inner {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    text-align: center;
}



/* Mirror of Nuxt UI's close button (absolute top-4 end-4 = 16px) on the
   opposite corner. Positioned against the modal content, not the padded body.
   The 36px flex box matches the close button's height so the back label sits
   at the same vertical center as the ✕. */
.modal-shell-2026__back {
    position: absolute;
    top: 28px;
    left: 28px;
    z-index: 10;
    display: flex;
    align-items: center;
    min-height: 36px;
}

/* Nuxt UI's ghost button keeps a near-white resting fill, which reads as a white
   disc around the ✕. Declared here rather than as a utility in the shared class
   string, which Tailwind does not scan. */
.modal-shell-2026__close {
    background-color: transparent;
    /* Above __topfade (z-index 1). The strip's backdrop-filter blurs everything
       painted beneath it, and the ✕ is a z-index:auto fixed element — so without
       this the blur is applied to the ✕ itself and the glyph smears. */
    z-index: 2;
}

.modal-shell-2026__close:hover {
    background-color: var(--magenta);
    /* The glyph has to invert with it. The ✕ is an iconify span painted with
       `background-color: currentColor`, so it follows the BUTTON's colour — and
       the button's own `hover:text-white` never reaches it: that class is written
       in a JS string in shared/utilities/ui.js, which Tailwind does not scan, so
       the utility is never generated (the same trap that file documents for its
       bracketed colour values). Without this the ✕ stays magenta on a magenta
       disc and disappears (Jack, 2026-08-21). */
    color: #fff;
}

.modal-shell-2026__topfade {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    /* 28px offset + the 34px button = 62px; 90px leaves the glyph clear of the
       edge of the blur rather than ending flush against it. */
    height: 90px;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    pointer-events: none;
    z-index: 1;
}

.modal-shell-2026__headline {
    font-family: var(--font-hubspot-serif);
    font-weight: 400;
    font-size: 25px;
    line-height: 1.3;
    color: var(--magenta);
    text-align: center;
    margin-bottom: 24px;
    /* The serif display face is drawn with its own spacing; body's 0.45px is
       for 16px sans body copy and reads loose on a 25-30px headline. Set
       explicitly because inheriting it is exactly what went wrong. */
    letter-spacing: normal;
}

/* Mobile/Header/H4 is 25px; the 30px above is the desktop size (Nick's mobile
   type mockup, Figma 5999:149541 — headlines read too large on a phone). Sizes
   and line-heights only; that ticket explicitly excludes spacing. */
@media (min-width: 640px) {
    .modal-shell-2026__headline {
        font-size: 30px;
    }
}

.modal-shell-2026__body {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    color: var(--magenta);
}

.modal-shell-2026__actions {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-top: 32px;
}
</style>
